import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_insights: number
  actionable_insights: number
  insights_this_week: number
  insights_acted_upon: number
  avg_impact_score: number
  ai_confidence_avg: number
}

interface Insight {
  id: string
  title: string
  category: string
  impact: string
  confidence: number
  recommendation: string
}

interface Trend {
  trend: string
  direction: string
  impact: string
  confidence: number
}

export default function ExecutiveInsightsPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [insights, setInsights] = useState<Insight[]>([])
  const [trends, setTrends] = useState<Trend[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, insightsRes, trendsRes] = await Promise.all([
          fetch('/api/executiveinsights/statistics'),
          fetch('/api/executiveinsights/insights'),
          fetch('/api/executiveinsights/trends')
        ])
        setStatistics(await statsRes.json())
        setInsights(await insightsRes.json())
        setTrends(await trendsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getImpactBadge = (impact: string) => {
    const colors: Record<string, string> = {
      'Critical': 'bg-red-100 text-red-800',
      'High': 'bg-orange-100 text-orange-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800',
      'Positive': 'bg-green-100 text-green-800',
      'Negative': 'bg-red-100 text-red-800',
      'Neutral': 'bg-gray-100 text-gray-800'
    }
    return colors[impact] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Executive Insights...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Executive Insights</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_insights}</div>
              <div className="text-sm text-gray-500">Total Insights</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.actionable_insights}</div>
              <div className="text-sm text-gray-500">Actionable</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.insights_this_week}</div>
              <div className="text-sm text-gray-500">This Week</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.insights_acted_upon}</div>
              <div className="text-sm text-gray-500">Acted Upon</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.avg_impact_score}/10</div>
              <div className="text-sm text-gray-500">Avg Impact</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.ai_confidence_avg}%</div>
              <div className="text-sm text-gray-500">AI Confidence</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">AI-Generated Insights</h2></div>
            <div className="p-4 space-y-4">
              {insights.map((ins) => (
                <div key={ins.id} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{ins.title}</h3>
                      <span className="text-sm text-gray-500">{ins.category}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getImpactBadge(ins.impact)}`}>
                        {ins.impact}
                      </span>
                      <span className="text-sm text-gray-500">{ins.confidence}%</span>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-blue-800">Recommendation: </span>
                    <span className="text-sm text-blue-700">{ins.recommendation}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Trend Analysis</h2></div>
            <div className="p-4 space-y-3">
              {trends.map((t, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium">{t.trend}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        t.direction === 'Accelerating' || t.direction === 'Growing' || t.direction === 'Strong'
                          ? 'bg-green-100 text-green-800'
                          : t.direction === 'Increasing' ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {t.direction}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getImpactBadge(t.impact)}`}>
                        {t.impact}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">{t.confidence}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
